import * as React from 'react';
import type {Dispatch} from 'src/reducers';

import type {
  DynamicResponse,
  AutomationModulesContext,
  ReqResVariables,
  ReqResVariable,
  SimpleVariable,
} from 'src/components/lib/automation-module-registry/automation-module-registry';
import type {
  Swimlane,
  CompositeAutomationNode,
  CompositeAutomationNodeApi,
} from 'src/types/automation-workflow';
import {useSelector} from 'react-redux';

import {serializeQuery} from 'src/utils/query';
import {selectAllEntityTypes} from 'src/selectors/target-entity-types';
import {AutomationModulesRegistryContext} from 'src/components/lib/automation-module-registry/automation-module-registry';
import {getAutomationWorkflowSwimlanes} from 'src/action-creators/automation-workflow';
import {swimlanesToGraph, useSwimlaneNode} from 'src/utils/automation-workflow';

/** Automation Config Map Starts Here */
//this map is used to store the automation config for the given node type
const nodeAutomationConfigMap = new Map<CanvasNodeTypes, any>();

export const getAutomationConfig = async (nodeType: CanvasNodeTypes) => {
  //if it is not in the map, use the node type as the ui dir
  const nodeUIDir =
    nodeTypeToUIDirMap[nodeType as keyof typeof nodeTypeToUIDirMap] ?? nodeType;

  if (nodeAutomationConfigMap.has(nodeType)) {
    return nodeAutomationConfigMap.get(nodeType);
  }
  //NOTE:(diwakersurya) dynamic import is used to load the automation config for the given node type
  let automationConfig = null;
  try {
    automationConfig = await import(
      `src/components/automation/${nodeUIDir}/automation-config.ts`
    );
  } catch (_: any) {
    automationConfig = null;
  }
  // if (!automationConfig) {
  //   throw new Error('automation-config could not be loaded for node type: ' + nodeType);
  // }
  nodeAutomationConfigMap.set(nodeType, automationConfig);

  return automationConfig;
};
/** Automation Config Map Ends Here */

//these are the node types which have been named differently on UI and backend
export const nodeTypeToUIDirMap = {
  'delay-wait': 'delay',
} as const;

export const defaultNodeUISectionMap = {
  settings: 'settings',
  content: 'content',
  details: 'details',
  schedule: 'schedule',
} as const;

//these node types exist in the automation system
export const canvasNodeTypes = [
  'trigger',
  'delay-wait',
  'email',
  'sms',
  'candidate-matching',
  'update-record',
  'create-ats-record',
  'custom-note-writeback',
  'candidate-evaluation',
  'candidate-evaluation-with-recruiter-hub',
  'foreach',
  'sms-chatbot',
  'voicebot',
  'voicebot-v2',
  'ai-generator',
  'whatsapp',
  'path',
  'decision',
  'split',
  'end',
  'chatbot-whatsapp',
  'recruiter-hub',
  'smart-scheduler',
  'connector',
  'crm',
] as const;

export type CanvasNodeTypes = typeof canvasNodeTypes[number];

export type UrlGenerator = {
  emailContentUrl: (
    automationWorkflowId: string,
    nodeId: string,
  ) => Promise<string | null | undefined>;
  nodeUISectionUrl: (params: {
    automationWorkflowId: string;
    swimlanes: Swimlane[];
    nodeId: string;
    nodeType: CanvasNodeTypes;
    query?: unknown;
    section: 'settings' | 'content' | 'details' | 'schedule' | string;
    // Required for `path` and `split` nodes — branchId for path, splitPath for split.
    transitionId?: string;
  }) => Promise<string | null | undefined>;
  workflowBlackoutSettingsUrl: (params: {
    automationWorkflowId: string;
  }) => Promise<string | null | undefined>;
};
export const getStringWithoutAutomationIdentifiers = (str: string): string => {
  return str.replace(/{{\s*[^}]+\s*}}/g, '');
};

export function usePreviousNodeInfo(
  nodeId: string,
): DynamicResponse | null | undefined {
  const contextValue = React.useContext<
    AutomationModulesContext<SimpleVariable>
  >(AutomationModulesRegistryContext);
  const {dynamicResponseSchema} = contextValue;
  const previousNodeInfo = dynamicResponseSchema?.[nodeId] ?? null;
  return previousNodeInfo;
}
export const NON_BLACKOUT_NODES = [
  'update-record',
  'custom-note-writeback',
  'candidate-matching',
  'ai-analyze',
  'candidate-evaluation',
  'candidate-evaluation-with-recruiter-hub',
] as const;

export const AI_NODES = [
  'voicebot',
  'voicebot-v2',
  'smart-scheduler',
  'candidate-matching',
  'candidate-evaluation',
  'candidate-evaluation-with-recruiter-hub',
] as const;

export const getUrlGenerator = (dispatch: Dispatch): UrlGenerator => {
  return {
    //NOTE:(diwakersurya) this is async because we need to fetch the swimlanes first
    // also this function has a lot of logic to handle the url generation specifically
    // to land the user to the correct node email editor page with the correct parameters
    // for now this is the only way and it is necessary to show the preview in case user
    // wants j2->j2 email content import
    emailContentUrl: async (
      automationWorkflowId: string,
      nodeId: string,
    ): Promise<string | null | undefined> => {
      const swimlanes: Swimlane[] = await dispatch(
        getAutomationWorkflowSwimlanes(automationWorkflowId),
      );

      if (swimlanes.length === 0) {
        return null;
      }
      //NOTE:(diwakersurya) for now we have only one swimlane
      const swid = swimlanes[0].id;

      const {nodes, edges} = swimlanesToGraph(swimlanes);
      const matchingNode = nodes.find((node) => node.id === nodeId);
      if (!matchingNode) {
        return null;
      }
      const edge = edges.find((edge) => edge.from === nodeId);
      if (!edge) {
        return null;
      }

      const sid = nodeId; // task node id is sid in this case
      const tid = edge.to;
      const an = nodeId;
      const prvid = nodeId;
      return `/automation-workflow/${automationWorkflowId}/email/${nodeId}/editor?${serializeQuery(
        {tid, sid, swid, an, fullScreenPanel: true, prvid},
      )}`;
    },
    nodeUISectionUrl: async ({
      automationWorkflowId,
      swimlanes,
      nodeId,
      nodeType,
      query,
      section,
      transitionId,
    }): Promise<string | null> => {
      //we will use the node type later.
      // for now just consider
      if (swimlanes.length === 0) {
        return null;
      }
      //NOTE:(diwakersurya) for now we have only one swimlane
      const swid = swimlanes[0].id;

      const {nodes, edges} = swimlanesToGraph(swimlanes);
      const matchingNode = nodes.find((node) => node.id === nodeId);
      if (!matchingNode) {
        return null;
      }
      const edge = edges.find((edge) => edge.from === nodeId);
      if (!edge) {
        return null;
      }

      const sid = nodeId; // task node id is sid in this case
      const tid = edge.to;
      const an = nodeId;
      const prvid = nodeId;

      const automationConfig = await getAutomationConfig(nodeType);

      const pathMap = {
        ...defaultNodeUISectionMap,
        ...(automationConfig?.nodeUISectionMap ?? {}),
      };
      const uiSectionPath = pathMap[section];
      if (!uiSectionPath) {
        return null;
      }
      // `path`, `decision` and `split` nodes route through an extra segment
      // between nodeId and the section: the INDEX of the transition within
      // next_nodes (path's `branchId` is the index; split's `splitPath` is
      // `String(index)` — see canvas-node.tsx parseFilter and
      // generateSplitsArray in utils/split.ts). Backend sends the raw
      // transition_id, so we resolve it to the index here.
      // `decision` shares path's routes (no `/decision/...` route exists),
      // so we emit `path` as the URL segment for both.
      const urlNodeType = nodeType === 'decision' ? 'path' : nodeType;
      const needsTransitionSegment =
        urlNodeType === 'path' || urlNodeType === 'split';
      let nodeSegment = nodeId;
      if (needsTransitionSegment) {
        if (transitionId == null) {
          return null;
        }
        const transitionIndex = (matchingNode.data.next_nodes ?? []).findIndex(
          (t) => String(t.transition_id) === String(transitionId),
        );
        if (transitionIndex === -1) {
          return null;
        }
        nodeSegment = `${nodeId}/${transitionIndex}`;
      }
      return `/automation-workflow/${automationWorkflowId}/${urlNodeType}/${nodeSegment}/${uiSectionPath}?${serializeQuery(
        {tid, sid, swid, an, prvid, ...(query ?? {})},
      )}`;
    },
    workflowBlackoutSettingsUrl: async ({
      automationWorkflowId,
    }): Promise<string | null> => {
      return `/automation-workflow/${automationWorkflowId}/blackout-settings`;
    },
  };
};
export function flattenReqResVariables(
  reqResVariables: ReqResVariables,
): Map<string, ReqResVariable> {
  const flattenedVariables = new Map();

  // Iterate through each node's variables
  reqResVariables.forEach((nodeVariables) => {
    // For each node, iterate through its variable groups
    nodeVariables.forEach((variableGroup) => {
      // Add each individual variable to the flattened map
      variableGroup.forEach((variable, key) => {
        flattenedVariables.set(key, variable);
      });
    });
  });

  return flattenedVariables;
}

export const getReqResVariableName = (
  reqResVariable: ReqResVariable,
  removeNodeIdentifier = false,
): string => {
  return `${
    removeNodeIdentifier
      ? ''
      : reqResVariable.node_name
      ? `${reqResVariable.node_name}.`
      : ''
  }${reqResVariable.moduleName ? `${reqResVariable.moduleName}.` : ''}${
    reqResVariable.selectionLevelTitle
      ? `${reqResVariable.selectionLevelTitle}.`
      : ''
  }${reqResVariable.title ?? ''}`;
};

export function extractVariableText(text: string): string | null | undefined {
  const regex = /{{\s*(.*?)\s*}}/;
  const match = text.match(regex);
  if (match && match[1]) {
    return match[1];
  } else {
    return null;
  }
}
export const isAutomationVariable = (variableId: string): boolean => {
  return variableId.startsWith('{{$.') && variableId.endsWith('}}');
};

export const extractAutomationModuleKey = (
  content: string,
): string | null | undefined => {
  if (!content) {
    return null;
  }

  // Extract chatbot_trigger_X or survey_trigger_X from patterns like
  // {{$.chatbot_trigger_X.response.long_url}} or {{$.survey_trigger_X.response.url}}
  const match = content.match(/\{\{\$\.([^.]+)\.response\.(long_url|url)\}\}/);

  return match ? match[1] : null;
};

export const getStringLengthWithAutomationIdentifiers = (
  value: string,
): number => {
  //we remove identifiers and then consider the length
  return getStringWithoutAutomationIdentifiers(value).length;
};

export const getNodeTypeFromNodeId = (nodeId: string): string => {
  const [nodeType, _] = nodeId.split('__');
  if (!nodeType) {
    throw new Error(`Invalid nodeId: ${nodeId}`);
  }
  return nodeType;
};

export type CanvasNodeProps = {
  nodeId: string;
  nodeTag: string | null | undefined;
  nodeName: string;
  swimlaneId: string;
  onNodeClick: () => unknown;
  onNodeScheduleClick: () => unknown;
  onNodeAlertsClick?: (() => unknown) | null | undefined;
  onNodeDeleteClick: () => unknown;
  onNodeDeleteAllBelowThisClick: () => unknown;
  active: boolean;
  error?: string | null | undefined;
  disabled?: boolean;
  disableContextButton?: boolean;
  disableContextMenu?: boolean;
  scheduleSeenOnce: boolean;
  completeNodeName: string;
  hfModeExceptions: Array<string> | null | undefined;
  isHfDeleteMode: boolean;
  nodeData: CompositeAutomationNode;
  nodeType: CanvasNodeTypes;
  nodePosition: {
    x: number;
    y: number;
  };
  hoverIcon?: string | null | undefined;
  hoverText?: string | null | undefined;
  onNodeNameEdit?: (newName: string) => Promise<unknown>;
  searched: boolean;
};

//NOTE:(diwakersurya) this function is used to get the node tracking id for the current node
// it is used in the candidate evaluation node to get the node tracking id for the previous node
// because the recipient id can be calculated only using this id. Ideally beckend should own
// the logic of recipient calculation based on the node id. ask before using it.
export function useGetNodeTrackingId(): (
  arg1: string,
) => string | null | undefined {
  const automationModulesRegistryContext = React.useContext(
    AutomationModulesRegistryContext,
  );

  return (nodeId: string): string | null | undefined => {
    const previousNodeInfo =
      automationModulesRegistryContext.getPreviousNodeInfo(nodeId)?.[nodeId];
    const trackingId = previousNodeInfo?.details.previous_node_context?.key;
    return trackingId;
  };
}

export const checkIfValidCanvasNodeType = (
  nodeTypeIdentifier: string,
): boolean => {
  if (
    nodeTypeIdentifier === 'trigger' ||
    nodeTypeIdentifier === 'delay-wait' ||
    nodeTypeIdentifier === 'email' ||
    nodeTypeIdentifier === 'sms' ||
    nodeTypeIdentifier === 'candidate-matching' ||
    nodeTypeIdentifier === 'update-record' ||
    nodeTypeIdentifier === 'create-ats-record' ||
    nodeTypeIdentifier === 'custom-note-writeback' ||
    nodeTypeIdentifier === 'foreach' ||
    nodeTypeIdentifier === 'sms-chatbot' ||
    nodeTypeIdentifier === 'ai-generator' ||
    nodeTypeIdentifier === 'voicebot' ||
    nodeTypeIdentifier === 'candidate-evaluation' ||
    nodeTypeIdentifier === 'candidate-evaluation-with-recruiter-hub' ||
    nodeTypeIdentifier === 'whatsapp' ||
    nodeTypeIdentifier === 'filter' ||
    nodeTypeIdentifier === 'split' ||
    nodeTypeIdentifier === 'end' ||
    nodeTypeIdentifier === 'chatbot-whatsapp' ||
    nodeTypeIdentifier === 'voicebot-v2' ||
    nodeTypeIdentifier === 'recruiter-hub' ||
    nodeTypeIdentifier === 'smart-scheduler' ||
    nodeTypeIdentifier === 'connector' ||
    nodeTypeIdentifier === 'crm'
  ) {
    return true;
  }
  return false;
};

/**NOTE:(diwakersurya) this hook is used to prepare the edit from the swimlane node
 * it is used to get the node data from the swimlane node and then prepare the edit
 * for the node (e.g. setting up the store).
 */
export const usePrepareEditFromSwimlaneNode = (
  swid: string,
  nodeId: string,
): {
  isPreparingEdit: boolean;
  data: CompositeAutomationNodeApi | null | undefined;
} => {
  const [isPreparingEdit, setIsPreparingEdit] = React.useState(true);
  const swimlaneNode = useSwimlaneNode(swid, nodeId);
  React.useEffect(() => {
    if (swimlaneNode) {
      setIsPreparingEdit(false);
    }
  }, [swimlaneNode]);

  return {
    isPreparingEdit,
    data: swimlaneNode,
  };
};

export const formatSpecificDateWithHoursMinutes = (date: string): string => {
  return `${date} 00:00:00`;
};

export const formatToSpecificDateFormat = (date: string): string => {
  return date.split(' ')[0];
};

export const getCompleteName = (
  nodeName: string,
  displayName: string,
): string => {
  return `${nodeName} ${displayName}`;
};

export type NodeIconConfig = {
  primaryIcon: {name: string; type: 'solid' | 'brands' | 'regular'};
  color: 'success' | 'information' | 'neutral' | 'clickable';
};

export const NODE_TYPE_ICON_MAP: Record<string, NodeIconConfig> = {
  trigger: {primaryIcon: {name: 'play', type: 'solid'}, color: 'success'},
  'ai-generator': {
    primaryIcon: {name: 'sparkles', type: 'solid'},
    color: 'clickable',
  },
  'candidate-evaluation': {
    primaryIcon: {name: 'id-card', type: 'solid'},
    color: 'clickable',
  },
  'candidate-evaluation-with-recruiter-hub': {
    primaryIcon: {name: 'id-card', type: 'solid'},
    color: 'clickable',
  },
  'chatbot-whatsapp': {
    primaryIcon: {name: 'robot', type: 'solid'},
    color: 'information',
  },
  'candidate-matching': {
    primaryIcon: {name: 'user', type: 'solid'},
    color: 'clickable',
  },
  'custom-note-writeback': {
    primaryIcon: {name: 'edit', type: 'solid'},
    color: 'information',
  },
  'delay-wait': {
    primaryIcon: {name: 'pause', type: 'solid'},
    color: 'neutral',
  },
  'create-ats-record': {
    primaryIcon: {name: 'layer-plus', type: 'solid'},
    color: 'information',
  },
  'smart-scheduler': {
    primaryIcon: {name: 'calendar', type: 'solid'},
    color: 'clickable',
  },
  foreach: {
    primaryIcon: {name: 'function', type: 'solid'},
    color: 'neutral',
  },
  'sms-chatbot': {
    primaryIcon: {name: 'robot', type: 'solid'},
    color: 'information',
  },
  split: {primaryIcon: {name: 'percent', type: 'solid'}, color: 'neutral'},
  voicebot: {
    primaryIcon: {name: 'phone-volume', type: 'solid'},
    color: 'clickable',
  },
  'voicebot-v2': {
    primaryIcon: {name: 'phone-volume', type: 'solid'},
    color: 'clickable',
  },
  whatsapp: {
    primaryIcon: {name: 'whatsapp', type: 'brands'},
    color: 'information',
  },
  email: {
    primaryIcon: {name: 'envelope', type: 'solid'},
    color: 'information',
  },
  sms: {primaryIcon: {name: 'message', type: 'solid'}, color: 'information'},
  path: {primaryIcon: {name: 'filter', type: 'solid'}, color: 'neutral'},
  'update-record': {
    primaryIcon: {name: 'database', type: 'solid'},
    color: 'information',
  },
  connector: {
    primaryIcon: {name: 'plug', type: 'solid'},
    color: 'information',
  },
  crm: {
    primaryIcon: {name: 'address-book', type: 'solid'},
    color: 'information',
  },
};

export const useEffectivePrimaryAndSecondaryEntityTypes = (
  anchorEntityType?: string,
  triggerEntityType?: string,
): {primaryEntityType?: string; secondaryEntityType?: string} => {
  const allEntityTypes = useSelector(selectAllEntityTypes);
  if (anchorEntityType === 'sense_ai_applicant') {
    const candidateEntityType = allEntityTypes.find(
      (entityType) => entityType.standard_entity_type === 'candidate',
    );
    const jobEntityType = allEntityTypes.find(
      (entityType) => entityType.standard_entity_type === 'job',
    );

    if (candidateEntityType && jobEntityType) {
      return {
        primaryEntityType: candidateEntityType.entity_type,
        secondaryEntityType: jobEntityType.entity_type,
      };
    }
  }

  return {
    primaryEntityType: triggerEntityType,
    secondaryEntityType: anchorEntityType,
  };
};

export const convertUsedInToUsageContext = async (
  usedIn: string,
): Promise<'settings' | 'logical'> => {
  if (usedIn === 'conditions') {
    return 'logical';
  }
  return 'settings';
};

export function escapeVarNames(varName: string): string {
  return varName.replace(/\./g, '\\.');
}
